import React, { useEffect, useState } from 'react';
import { addToRange, DayPicker } from 'react-day-picker';
import 'react-day-picker/style.css';
import './DateRangePickerUi.css';
import { startOfDay, endOfDay } from 'date-fns';
import { useTranslate } from '../../hooks/context/useTranslate';
import { IonItem, IonList } from '@ionic/react';
import { useUserState } from '../../hooks/context/useUserState';
import { enUS } from 'date-fns/locale';
import {
  DATASET_DAY_PRESETS,
  DATASET_RANGE_PRESETS,
  DAY_PRESETS,
  LANGUAGES,
  RANGE_PRESETS,
} from '../../constants';
import { DayPreset, RangePreset } from '../../types/types';
import { getDateString } from '../../utils/filters/getDateString';
import { getTimespanFromRange } from '../../utils/filters/getTimespanFromRange';
import { getRangeForTimespan } from '../../utils/filters/getRangeForTimespan';

interface DateRangePickerProps {
  from: Date | undefined;
  to: Date | undefined;
  namedRange: string | undefined;
  handleRangeSet: (
    range: any,
    namedRangeSet: string | undefined,
    isPresetClick: boolean
  ) => void;
  allowRange?: boolean;
  defaultValue: Array<string> | null;
  defaultNamedRange: string | null;
  minValue: Date | null;
  maxValue: Date | null;
  autoselectRange?: boolean;
  isDatasetFilter?: boolean;
}

export default function DateRangePickerUi({
  from,
  to,
  namedRange,
  handleRangeSet,
  allowRange = true,
  defaultValue,
  defaultNamedRange,
  minValue,
  maxValue,
  autoselectRange = false,
  isDatasetFilter = false,
}: DateRangePickerProps) {
  const {
    state: { language },
  } = useUserState();
  const [currentMonth, setCurrentMonth] = useState(from || new Date());
  const [locale, setLocale] = useState(enUS);
  const { t } = useTranslate();

  useEffect(() => {
    const selectedLanguage = LANGUAGES.find(({ id }) => id === language);
    if (selectedLanguage) {
      setLocale(selectedLanguage?.dateLocale);
    }
  }, [language]);

  // Check if selected date/range is equal to some preset
  useEffect(() => {
    if (!from && !to) {
      if (defaultNamedRange) {
        onRangePresetClick(defaultNamedRange);
      } else if (defaultValue) {
        if (allowRange) {
          const from = new Date(defaultValue[0]);
          const to = new Date(defaultValue[1]);
          const foundNamedRange = getTimespanFromRange(from, to, true);
          const rangeId = foundNamedRange ? foundNamedRange.id : null;
          handleRangeSet({ from, to }, rangeId, false);
          setCurrentMonth(from);
        } else {
          const day = new Date(defaultValue[0]);
          const foundNamedRange = getTimespanFromRange(day, day, false);
          const rangeId = foundNamedRange ? foundNamedRange.id : null;
          handleRangeSet(day, rangeId, false);
          setCurrentMonth(day);
        }
      } else {
        onRangePresetClick(
          allowRange ? RangePreset.CURRENT_WEEK : DayPreset.CURRENT_DAY
        );
      }
    } else if (!allowRange && from) {
      setCurrentMonth(from);
    }
  }, [from, to]);

  useEffect(() => {
    if (namedRange) {
      onRangePresetClick(namedRange);
    }
  }, [namedRange]);

  function handleDayClick(day) {
    if (allowRange) {
      const newRange = addToRange(startOfDay(day), { from, to });
      if (newRange) {
        if (newRange.from) {
          newRange.from = startOfDay(newRange.from);
        }
        if (newRange.to) {
          newRange.to = endOfDay(newRange.to);
        } else if (newRange.from && !newRange.to) {
          newRange.to = endOfDay(newRange.from);
        }
        const foundNamedRange = getTimespanFromRange(
          newRange.from,
          newRange.to,
          true
        );
        const rangeId = foundNamedRange ? foundNamedRange.id : null;
        handleRangeSet(newRange, rangeId, false);
      } else {
        handleRangeSet({ from: undefined, to: undefined }, namedRange, false);
      }
    } else {
      if (day) {
        const foundNamedRange = getTimespanFromRange(day, day, false);
        const rangeId = foundNamedRange ? foundNamedRange.id : null;
        const dayStart = startOfDay(day);
        handleRangeSet(dayStart, rangeId, false);
      } else {
        handleRangeSet(undefined, undefined, false);
      }
    }
  }
  const onRangePresetClick = (elemId) => {
    const range = getRangeForTimespan(elemId);
    if (allowRange) {
      handleRangeSet(range, elemId, true);
    } else {
      handleRangeSet(range.from, elemId, true);
    }
    if (range && range.from) {
      setCurrentMonth(range.from);
    }
  };

  const disabled: any = {};
  if (minValue) {
    disabled.before = minValue;
  }
  if (maxValue) {
    disabled.after = maxValue;
  }

  let presets;
  if (allowRange) {
    presets = isDatasetFilter ? DATASET_RANGE_PRESETS : RANGE_PRESETS;
  } else {
    presets = isDatasetFilter ? DATASET_DAY_PRESETS : DAY_PRESETS;
  }
  return (
    <div className="daypicker-wrapper-controlls">
      {allowRange ? (
        <IonList className="filter-options">
          {presets.map((elem) => {
            let isChosen;
            if (autoselectRange) {
              const range = getRangeForTimespan(elem.id);
              isChosen =
                range.from &&
                range.to &&
                from &&
                to &&
                getDateString(range.from) === getDateString(from) &&
                getDateString(range.to) === getDateString(to);
            } else {
              isChosen = elem.id === namedRange;
            }
            return (
              <IonItem
                button={true}
                lines={isChosen ? 'full' : 'none'}
                key={elem.id}
                onClick={() => onRangePresetClick(elem.id)}
                className={`${isChosen && 'chosen'}`}
              >
                {t(elem.name)}
              </IonItem>
            );
          })}
        </IonList>
      ) : (
        <IonList className="filter-options">
          {presets.map((elem) => {
            let isChosen;
            if (autoselectRange) {
              const range = getRangeForTimespan(elem.id);
              isChosen =
                range.from &&
                from &&
                getDateString(range.from) === getDateString(from);
            } else {
              isChosen = elem.id === namedRange;
            }
            return (
              <IonItem
                button={true}
                lines={isChosen ? 'full' : 'none'}
                key={elem.id}
                onClick={() => onRangePresetClick(elem.id)}
                className={`${isChosen && 'chosen'}`}
              >
                {t(elem.name)}
              </IonItem>
            );
          })}
        </IonList>
      )}
      {allowRange ? (
        <DayPicker
          modifiers={{
            selected: from && { from, to },
            range_start: from,
            range_end: to,
          }}
          onDayClick={handleDayClick}
          month={currentMonth}
          onMonthChange={setCurrentMonth}
          locale={locale}
          disabled={disabled}
        />
      ) : (
        <DayPicker
          mode={'single'}
          selected={from}
          onSelect={handleDayClick}
          month={currentMonth}
          onMonthChange={setCurrentMonth}
          locale={locale}
          disabled={disabled}
        />
      )}
    </div>
  );
}
